import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

/**
 * <HeroImage src={...} alt={...} />
 *
 * Sits in the lower-left of a service-page hero, balancing the composition.
 * Treatment (Ink & Electric):
 *   - 18px rounded panel, 1px border @ white/10, soft violet outer glow.
 *   - Photo desaturated, then a violet → electric duotone via mix-blend-mode.
 *   - Edges masked into the #0A0A0F background on two sides (no hard rect).
 *   - Faint noise + subtle dot grid texture.
 *   - Bright area positioned toward the upper-right (near the accent word).
 *
 * Motion (gated on prefers-reduced-motion: no-preference):
 *   - Fade up + un-blur on mount, delayed so the headline finishes first.
 *   - One scan-light sweep across the frame on load (~1.8s).
 *   - Mouse parallax — a few px translate toward the cursor.
 *   - Scroll parallax — drifts ~20px slower than the text.
 *   - Responsive: mobile = full-width below text, glow softened.
 */
export function HeroImage({
  src,
  alt,
  className = '',
  loadDelayMs = 850,
  testId = 'hero-image',
}) {
  const wrapRef = useRef(null);
  const imgWrapRef = useRef(null);
  const reduced = useReducedMotion();
  const [revealed, setRevealed] = useState(false);
  const [swept, setSwept] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Reveal + scan sweep, after the headline words have animated in.
  useEffect(() => {
    const t1 = setTimeout(() => setRevealed(true), reduced ? 0 : loadDelayMs);
    const t2 = setTimeout(() => setSwept(true), reduced ? 0 : loadDelayMs + 250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduced, loadDelayMs]);

  // Mouse parallax (few px) on the inner image wrapper.
  useEffect(() => {
    if (reduced) return;
    const el = wrapRef.current;
    const inner = imgWrapRef.current;
    if (!el || !inner) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const dx = ((e.clientX - r.left) / r.width - 0.5) * 10;
      const dy = ((e.clientY - r.top) / r.height - 0.5) * 10;
      inner.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(1.04)`;
    };
    const onLeave = () => { inner.style.transform = ''; };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [reduced]);

  // Scroll parallax — wrapper drifts ~20px slower than text.
  useEffect(() => {
    if (reduced) return;
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    let targetY = 0;
    let currentY = 0;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Centre of element relative to viewport, range roughly -1..1
      const rel = (rect.top + rect.height / 2) / vh - 0.5;
      targetY = -rel * 28; // up to ~14px each direction
    };
    const tick = () => {
      currentY += (targetY - currentY) * 0.12;
      el.style.setProperty('--scroll-y', `${currentY.toFixed(2)}px`);
      raf = requestAnimationFrame(tick);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      className={`relative w-full aspect-[5/4] md:aspect-[5/4] ${className}`}
      style={{
        // Parallax offset + reveal transform composed together
        transform: `translateY(calc(var(--scroll-y, 0px) + ${revealed ? '0px' : '24px'}))`,
        opacity: revealed ? 1 : 0,
        filter: revealed ? 'blur(0)' : 'blur(10px)',
        transition: reduced
          ? 'none'
          : 'opacity 900ms cubic-bezier(.2,.65,.2,1), filter 900ms cubic-bezier(.2,.65,.2,1), transform 900ms cubic-bezier(.2,.65,.2,1)',
        willChange: 'transform, opacity, filter',
      }}
    >
      {/* Outer panel: rounded, border, soft violet outer glow, edge mask */}
      <div
        className="relative h-full w-full overflow-hidden rounded-[18px] border border-white/[0.10]"
        style={{
          boxShadow:
            '0 30px 90px -30px rgba(108,92,231,0.55), 0 10px 40px -20px rgba(75,159,255,0.30), inset 0 1px 0 rgba(255,255,255,0.04)',
          // Mask edges so the photo fades into #0A0A0F on two+ sides.
          WebkitMaskImage:
            'radial-gradient(120% 110% at 65% 35%, #000 55%, rgba(0,0,0,0.45) 78%, transparent 100%)',
          maskImage:
            'radial-gradient(120% 110% at 65% 35%, #000 55%, rgba(0,0,0,0.45) 78%, transparent 100%)',
        }}
      >
        {/* Image layer (desaturated, contained inside an inner wrapper for parallax) */}
        <div
          ref={imgWrapRef}
          className="absolute inset-0 transition-transform duration-700 ease-out"
          style={{ willChange: 'transform' }}
        >
          {/* image element */}
          <img
            src={src}
            alt={alt}
            onLoad={() => setLoaded(true)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="absolute inset-0 h-full w-full object-cover scale-[1.04]"
            style={{
              filter: 'grayscale(0.95) contrast(1.05) brightness(0.78)',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 600ms ease',
            }}
            data-testid={`${testId}-img`}
          />
          {/* Fallback gradient if the image fails to load */}
          {!loaded && (
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(circle at 30% 25%, rgba(108,92,231,0.35), transparent 55%), radial-gradient(circle at 80% 80%, rgba(75,159,255,0.25), transparent 55%), #0E0E15',
              }}
            />
          )}
        </div>

        {/* Duotone overlay — violet → electric blue, via screen blend */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #6C5CE7 0%, #4B9FFF 100%)',
            mixBlendMode: 'screen',
            opacity: 0.55,
          }}
        />
        {/* Deep colour tint to seat the duotone */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(108,92,231,0.45) 0%, rgba(10,10,15,0.35) 60%, rgba(75,159,255,0.20) 100%)',
            mixBlendMode: 'multiply',
          }}
        />

        {/* Subtle dot grid texture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)',
            backgroundSize: '4px 4px',
            mixBlendMode: 'overlay',
          }}
        />

        {/* Film grain / noise — kept faint (~4%) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            opacity: 0.4,
            mixBlendMode: 'overlay',
          }}
        />

        {/* Highlight: brightest spot near the upper-right (near accent word) */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 md:h-64 md:w-64 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(108,92,231,0.45) 0%, rgba(108,92,231,0) 70%)',
            mixBlendMode: 'screen',
          }}
        />

        {/* Scan-light sweep — fires once on load */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div
            data-testid={`${testId}-sweep`}
            className="absolute top-0 bottom-0 w-[42%] -skew-x-12"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 45%, rgba(108,92,231,0.18) 55%, transparent 100%)',
              transform: swept ? 'translateX(260%)' : 'translateX(-100%)',
              transition: reduced ? 'none' : 'transform 1800ms cubic-bezier(.2,.65,.2,1)',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        {/* Hairline accent rule along the top edge */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-6 right-6 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.30) 30%, rgba(108,92,231,0.55) 65%, transparent 100%)',
          }}
        />

        {/* Caption-style numeric tag, bottom-left */}
        <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2.5 text-[10px] uppercase tracking-[0.28em] text-white/80 bg-black/30 backdrop-blur-sm border border-white/[0.12] rounded-full px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-violet" />
          NIO/STUDIO
        </div>
      </div>
    </div>
  );
}
